// src/portal/vendor/components/sidebar/VendorSidebar.jsx
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RiDashboardLine,
  RiHammerLine,
  RiUserLine,
  RiLogoutBoxLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiMenu4Line,
  RiSettings4Line,
  RiFileTextLine } from
'react-icons/ri';
import api from '../../../../services/api';
import { useTheme } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { formatRoleName } from '../../../../utils/roles';

const VendorSidebar = ({ isOpen, setIsOpen, isMobile }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState('Vendor Portal');

  useEffect(() => {
    if (!user) return;
    const fetchOrgSettings = async () => {
      try {
        const response = await api.get('/organization/settings');
        if (response.data.success && response.data.organization) {
          setOrgName(response.data.organization.name);
        }
      } catch (error) {
        console.error('Error fetching organization name:', error);
      }
    };
    fetchOrgSettings();
  }, [user]);

  const toggleSidebar = () => setIsOpen(!isOpen);

  const menuItems = [
  {
    section: 'core',
    items: [
    {
      path: '/portal/vendor/dashboard',
      name: t('dashboard'),
      icon: <RiDashboardLine size={20} />,
      exact: true
    }]

  },
  {
    section: 'operations',
    items: [
    {
      path: '/portal/vendor/work-orders',
      name: t('work_orders') || 'Work Orders',
      icon: <RiHammerLine size={20} />
    }]

  },
  {
    section: 'account',
    items: [
    {
      path: '/portal/vendor/profile',
      name: t('profile'),
      icon: <RiUserLine size={20} />
    },
    {
      path: '/portal/vendor/w9',
      name: 'W-9 Tax Form',
      icon: <RiFileTextLine size={20} />
    },
    {
      path: '/portal/vendor/settings',
      name: t('settings'),
      icon: <RiSettings4Line size={20} />
    }]

  }];


  const sidebarVariants = {
    open: { width: 280, x: 0 },
    closed: { width: isMobile ? 280 : 80, x: isMobile ? isRTL ? '100%' : '-100%' : 0 }
  };

  const textVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: -10 }
  };

  return (
    <motion.aside
      variants={sidebarVariants}
      animate={isOpen ? 'open' : 'closed'}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`h-screen ${isDarkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-gray-200'} ${isRTL ? 'border-l' : 'border-r'} flex flex-col shadow-xl fixed z-50`}
      style={{ top: 0, bottom: 0, [isRTL ? 'right' : 'left']: 0 }}>
      
      <div className={`flex items-center justify-between h-16 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-b px-4`}>
        <div className={`flex items-center ${isOpen ? 'gap-3 overflow-hidden' : 'justify-center w-full'}`}>
          <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg shadow-red-600/20">
            <RiHammerLine className="text-white" size={18} />
          </div>
          <AnimatePresence>
            {isOpen &&
            <motion.span
              variants={textVariants}
              initial="closed"
              animate="open"
              className={`text-lg font-bold truncate ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              
                {orgName}
              </motion.span>
            }
          </AnimatePresence>
        </div>
        {isOpen &&
        <button onClick={toggleSidebar} className={`p-1.5 rounded-full ${isDarkMode ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <RiArrowLeftSLine size={24} />
          </button>
        }
      </div>

      {!isOpen &&
      <button onClick={toggleSidebar} className={`p-2 mx-auto mt-4 rounded-full ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-500'}`}>
          <RiMenu4Line size={20} />
        </button>
      }

      <div className={`p-4 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-b`}>
        <div className={`flex items-center ${isOpen ? '' : 'justify-center'}`}>
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white font-bold shadow-md">
            {user?.name?.charAt(0) || 'V'}
          </div>
          {isOpen &&
          <div className="ml-3 overflow-hidden">
               <p className={`text-sm font-bold truncate ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{user?.name}</p>
               <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">{user?.role || 'Vendor'}</p>
            </div>
          }
        </div>
      </div>

      <nav className="flex-grow overflow-y-auto px-4 py-6 scrollbar-hide">
        {menuItems.map((section, idx) =>
        <div key={idx} className="mb-6">
            {isOpen && <h3 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3 ml-2">{section.section}</h3>}
            <ul className="space-y-1">
              {section.items.map((item) =>
            <li key={item.path}>
                  <NavLink
                to={item.path}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                isActive ?
                isDarkMode ? 'bg-red-500/10 text-red-500' : 'bg-red-50 text-red-600 font-semibold' :
                isDarkMode ? 'text-zinc-400 hover:bg-zinc-900 hover:text-white' : 'text-zinc-600 hover:bg-gray-50 hover:text-zinc-900'}`
                }>
                
                    <span className="flex-shrink-0">{item.icon}</span>
                    {isOpen && <span className="text-sm">{item.name}</span>}
                  </NavLink>
                </li>
            )}
            </ul>
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-zinc-800/50">
        <button
          onClick={() => {logout();navigate('/portal/vendor/login');}}
          className={`flex items-center gap-3 w-full px-3 py-3 rounded-xl transition-all ${isDarkMode ? 'text-zinc-500 hover:bg-red-500/10 hover:text-red-400' : 'text-zinc-500 hover:bg-gray-100'}`}>
          
          <RiLogoutBoxLine size={20} />
          {isOpen && <span className="text-sm font-medium">{t("sign_out_2")}</span>}
        </button>
      </div>
    </motion.aside>);

};

export default VendorSidebar;